import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCheck, FiX, FiCalendar, FiUsers, FiClock } from 'react-icons/fi';
import apiService from '../../services/api';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import logger from '../../utils/logger';

const AttendanceContainer = styled.div`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const AttendanceHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  h2 {
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.125rem;
    }
  }
`;

const AttendanceStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background-color: var(--background-secondary);
  border-bottom: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 1rem;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0.75rem;
    gap: 0.5rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => {
    switch (props.type) {
      case 'present': return 'var(--success-color)';
      case 'absent': return 'var(--error-color)';
      case 'rate': return 'var(--primary-color)';
      default: return 'var(--text-primary)';
    }
  }};
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const AttendanceList = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const AttendanceItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  margin-bottom: 0.75rem;
  background-color: var(--background-primary);
  transition: var(--transition);
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-sm);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const SessionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SessionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 0;
  background-color: ${props => {
    switch (props.$status) {
      case 'present': return 'rgba(104, 211, 145, 0.1)';
      case 'absent': return 'rgba(252, 129, 129, 0.1)';
      default: return 'var(--background-tertiary)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'present': return 'var(--success-dark)';
      case 'absent': return 'var(--error-color)';
      default: return 'var(--text-muted)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'present': return 'var(--success-color)';
      case 'absent': return 'var(--error-color)';
      default: return 'var(--border-color)';
    }
  }};
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const SessionDetails = styled.div`
  flex: 1;
`;

const SessionTitle = styled.h4`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const SessionDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--text-muted);
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.$status) {
      case 'present': return 'rgba(104, 211, 145, 0.1)';
      case 'absent': return 'rgba(252, 129, 129, 0.1)';
      default: return 'var(--background-tertiary)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'present': return 'var(--success-dark)';
      case 'absent': return 'var(--error-color)';
      default: return 'var(--text-muted)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'present': return 'var(--success-color)';
      case 'absent': return 'var(--error-color)';
      default: return 'var(--border-color)';
    }
  }};
  
  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8rem;
    align-self: flex-end;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    
    svg {
      width: 40px;
      height: 40px;
    }
    
    p {
      font-size: 0.875rem;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--primary-color);
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-muted);
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
    font-size: 0.875rem;
  }
`;

function CourseAttendanceRecords({ courseId, courseName, electiveId, electiveName, isElective = false }) {
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const entityId = isElective ? electiveId : courseId;
  const entityName = isElective ? electiveName : courseName;

  const fetchAttendanceData = useCallback(async (id, isElective) => {
    try {
      setLoading(true);
      setError('');
      
      const response = isElective 
        ? await apiService.getStudentElectiveAttendance(id)
        : await apiService.getStudentAttendance(id);
      
      if (response.success) {
        setAttendanceData(response.attendance);
      } else {
        setError(response.message || 'Failed to fetch attendance data');
      }
    } catch (error) {
      logger.error('Error fetching attendance data', { error, entityId, isElective });
      setError('Failed to fetch attendance data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  // Fetch attendance data for the course or elective
  useEffect(() => {
    if (entityId) {
      fetchAttendanceData(entityId, isElective);
    }
  }, [entityId, isElective, fetchAttendanceData]);


  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <FiCheck size={16} />;
      case 'absent':
        return <FiX size={16} />;
      default:
        return <FiClock size={16} />;
    }
  };

  // Calculate attendance statistics
  const calculateStats = () => {
    if (!attendanceData || !attendanceData.history) {
      return { presentCount: 0, absentCount: 0, totalSessions: 0, attendanceRate: 0 };
    }

    const history = attendanceData.history;
    const presentCount = history.filter(record => record.status === 'present').length;
    const absentCount = history.filter(record => record.status === 'absent').length;
    const totalSessions = history.length;
    const attendanceRate = totalSessions > 0 ? Math.round((presentCount / totalSessions) * 100) : 0;

    return { presentCount, absentCount, totalSessions, attendanceRate };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <AttendanceContainer>
        <AttendanceHeader>
          <h2>
            <FiUsers size={20} />
            Attendance Records
          </h2>
        </AttendanceHeader>
        <LoadingState>
          <LoadingSpinner />
          <span>Loading attendance records...</span>
        </LoadingState>
      </AttendanceContainer>
    );
  }

  if (error) {
    return (
      <AttendanceContainer>
        <AttendanceHeader>
          <h2>
            <FiUsers size={20} />
            Attendance Records
          </h2>
        </AttendanceHeader>
        <EmptyState>
          <FiX />
          <p>{error}</p>
        </EmptyState>
      </AttendanceContainer>
    );
  }

  return (
    <AttendanceContainer>
      <AttendanceHeader>
        <h2>
          <FiUsers size={20} />
          Attendance Records
        </h2>
      </AttendanceHeader>

      <AttendanceStats>
        <StatItem>
          <StatValue type="rate">{stats.attendanceRate}%</StatValue>
          <StatLabel>Attendance Rate</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue type="present">{stats.presentCount}</StatValue>
          <StatLabel>Present</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue type="absent">{stats.absentCount}</StatValue>
          <StatLabel>Absent</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.totalSessions}</StatValue>
          <StatLabel>Total Sessions</StatLabel>
        </StatItem>
      </AttendanceStats>

      {attendanceData && attendanceData.history && attendanceData.history.length > 0 ? (
        <AttendanceList>
          {attendanceData.history.map((record, index) => (
            <AttendanceItem
              key={record.session_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <SessionInfo>
                <SessionIcon $status={record.status}>
                  {getStatusIcon(record.status)}
                </SessionIcon>
                <SessionDetails>
                  <SessionTitle>
                    {record.title || `${entityName} Session`}
                  </SessionTitle>
                  <SessionDate>
                    <FiCalendar size={12} />
                    {formatDateForDisplay(record.date, 'MMM d, yyyy')}
                  </SessionDate>
                </SessionDetails>
              </SessionInfo>
              <StatusBadge $status={record.status}>
                {getStatusIcon(record.status)}
                {record.status === 'present' ? 'Present' : 'Absent'}
              </StatusBadge>
            </AttendanceItem>
          ))}
        </AttendanceList>
      ) : (
        <EmptyState>
          <FiCalendar />
          <p>No attendance records found for {entityName}.</p>
        </EmptyState>
      )}
    </AttendanceContainer>
  );
}

export default CourseAttendanceRecords;
